import React, { memo } from 'react'
import tw from 'twin.macro'
import { TabsSelectorItem } from './TabsSelectorItem'
import type { TabsSelectorOption } from './TabsSelector.types'

interface Props<V extends string> {
  options: TabsSelectorOption<V>[]
  selectedValue: V
  onSelect(value: V): void
  className?: any
  /** Prefix for QA: tabs get `${prefix}-tab-${value}`. */
  testIdPrefix?: string
}

/**
 * apps/dashboard's `TabsSelector` (the underlined tabs on its Payments and Team pages), without
 * the overflow menu — this app's tab sets are short. Text-only tabs on a hairline, the selected
 * one black with a 3px black indicator; unlike `TableTabs`, it carries no icon or count subtitle
 * and is meant to switch content sections, not table filters.
 */
const TabsSelectorComponent = <V extends string>({
  options,
  selectedValue,
  onSelect,
  className,
  testIdPrefix,
}: Props<V>) => {
  return (
    <div
      role="tablist"
      css={[tw`flex flex-row border-0 border-b border-solid border-shades-200`, className]}
    >
      {options.map((option) => {
        return (
          <TabsSelectorItem
            key={option.value}
            label={option.label}
            value={option.value}
            count={option.count}
            selected={selectedValue === option.value}
            onSelect={onSelect}
            dataTestId={testIdPrefix ? `${testIdPrefix}-tab-${option.value}` : undefined}
          />
        )
      })}
    </div>
  )
}

export const TabsSelector = memo(TabsSelectorComponent) as typeof TabsSelectorComponent
